import React, { useCallback } from 'react';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import availableTimes from '@atlassian/jira-common-constants/src/datetimepicker-available-times.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { standardizeLocale } from '@atlassian/jira-issue-format-date/src/common/utils.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import type { Data } from '@atlassian/jira-platform-services-user-current/src/types.tsx';
import { ObjectDetailsSpacer } from '@atlassian/jira-servicedesk-insight-attribute-views/src/common/ui/styled.tsx';
import { DateTimeReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/date-time/index.tsx';
import {
	useDefaultAttributeValue,
	useDefaultAttributeDateValue,
	getDefaultAttributeValue,
	getDefaultAttributeDateValue,
} from '@atlassian/jira-servicedesk-insight-object-attribute-utils/src/utils/default/index.tsx';
import { useEditableAttribute } from '@atlassian/jira-servicedesk-insight-object-details-store/src/controllers/use-editable-attribute/index.tsx';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import {
	parseDateTimeAttributeValueDeprecated,
	useParseDateTimeAttributeValue,
} from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/utils/use-date-formatters/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { InlineEditableAttribute } from '../../common/ui/editable-attribute/index.tsx';

type Props = {
	isEditable: boolean;
	attributeType: DefaultAttributeType;
};

// Extracted for simpler injection in storybooks
export const useCurrentUserTimezone = () => {
	const { data } = useCurrentUser();

	const isFullUserData = (user: unknown): user is Data =>
		typeof user === 'object' && !!user && 'timeZone' in user;

	const timeZone = isFullUserData(data.user) ? data.user.timeZone : null;

	if (!timeZone) {
		// This code path should never be evaluated as timeZone comes from
		// tenant user context. Flow sees the timezone value as nullable as it's
		// initialised from an empty sweet-state store, but that context comes
		// from meta tags embedded on the page that are populated into the store
		// at runtime.
		throw new Error('Timezone not found. Please mock useCurrentUserTimezone for storybooks/tests');
	}

	return timeZone;
};

const DateTimeAttributeOld = (props: Props) => {
	const { attributeType, isEditable } = props;
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const timeZone = useCurrentUserTimezone();

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useDefaultAttributeValue(attribute);

	const onValueChange = useCallback(
		(val: string) => {
			setValue(val);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getDefaultAttributeValue(attribute)),
		[attribute, setValue],
	);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute"
			value={value}
			onChange={onValueChange}
			storedValue={getDefaultAttributeValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			keepEditViewOpenOnBlur
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<DateTimeReadView value={value || null} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<DateTimePicker
					testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute.date-time-picker"
					autoFocus
					value={value}
					locale={standardizedLocale}
					onChange={onChange}
					isInvalid={hasValidationError}
					// @ts-expect-error - TS2322 - Type '(fieldValue: string) => { dateValue: string; timeValue: string; zoneValue: string; } | undefined' is not assignable to type '(dateTimeValue: string, date: string, time: string, timezone: string) => { dateValue: string; timeValue: string; zoneValue: string; }'.
					parseValue={(fieldValue) =>
						fieldValue !== ''
							? parseDateTimeAttributeValueDeprecated(fieldValue, timeZone)
							: undefined
					}
					timePickerProps={{
						times: availableTimes,
						timeIsEditable: true,
					}}
				/>
			)}
			onResetValue={resetValue}
		/>
	);
};

const DateTimeAttributeNew = (props: Props) => {
	const { attributeType, isEditable } = props;
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const timeZone = useCurrentUserTimezone();

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useDefaultAttributeDateValue(attribute);

	const onValueChange = useCallback(
		(val: string) => {
			setValue(val);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getDefaultAttributeDateValue(attribute)),
		[attribute, setValue],
	);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute"
			value={value}
			onChange={onValueChange}
			storedValue={getDefaultAttributeDateValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			keepEditViewOpenOnBlur
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<DateTimeReadView value={value || null} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<DateTimePicker
					testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute.date-time-picker"
					autoFocus
					value={value}
					locale={standardizedLocale}
					onChange={onChange}
					isInvalid={hasValidationError}
					// @ts-expect-error - TS2322 - Type '(fieldValue: string) => { dateValue: string; timeValue: string; zoneValue: string; } | undefined' is not assignable to type '(dateTimeValue: string, date: string, time: string, timezone: string) => { dateValue: string; timeValue: string; zoneValue: string; }'.
					parseValue={(fieldValue) =>
						fieldValue !== ''
							? parseDateTimeAttributeValueDeprecated(fieldValue, timeZone)
							: undefined
					}
					timePickerProps={{
						times: availableTimes,
						timeIsEditable: true,
					}}
				/>
			)}
			onResetValue={resetValue}
		/>
	);
};

const DateTimeAttribute = (props: Props) => {
	const { attributeType, isEditable } = props;
	const locale = useLocale();
	const standardizedLocale = standardizeLocale(locale);
	const { parseDateTimeAttributeValue } = useParseDateTimeAttributeValue();

	const [{ attribute }] = useEditableAttribute(attributeType);
	const [value, setValue] = useDefaultAttributeDateValue(attribute);

	const onValueChange = useCallback(
		(val: string) => {
			setValue(val);
		},
		[setValue],
	);

	const resetValue = useCallback(
		() => setValue(getDefaultAttributeDateValue(attribute)),
		[attribute, setValue],
	);

	return (
		<InlineEditableAttribute
			testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute"
			value={value}
			onChange={onValueChange}
			storedValue={getDefaultAttributeDateValue(attribute)}
			isEditable={isEditable}
			attributeType={attributeType}
			hideActionButtons={false}
			keepEditViewOpenOnBlur
			renderReadView={() => (
				<ObjectDetailsSpacer>
					<DateTimeReadView value={value || null} />
				</ObjectDetailsSpacer>
			)}
			renderEditView={({ hasValidationError, onChange }) => (
				<DateTimePicker
					testId="servicedesk-insight-inline-editable-attributes.ui.date-time-attribute.date-time-picker"
					autoFocus
					value={value}
					locale={standardizedLocale}
					onChange={onChange}
					isInvalid={hasValidationError}
					// @ts-expect-error - TS2322 - Type '(fieldValue: string) => { dateValue: string; timeValue: string; zoneValue: string; } | undefined' is not assignable to type '(dateTimeValue: string, date: string, time: string, timezone: string) => { dateValue: string; timeValue: string; zoneValue: string; }'.
					parseValue={(fieldValue) =>
						fieldValue !== '' ? parseDateTimeAttributeValue(fieldValue) : undefined
					}
					timePickerProps={{
						times: availableTimes,
						timeIsEditable: true,
					}}
				/>
			)}
			onResetValue={resetValue}
		/>
	);
};

export const DateTimeEditableAttribute = (props: Props) => (
	<JSErrorBoundary
		id="InsightInlineEditableDateAttribute"
		packageName="jiraServicedeskInsightEditableDateTimeAttribute"
		teamName="falcons"
		fallback="unmount"
	>
		{/* eslint-disable-next-line no-nested-ternary */}
		{fg('fly-2238-invalid-date') ? (
			fg('cmdb_datetime_attribute_timezone_fix') ? (
				<DateTimeAttribute {...props} />
			) : (
				<DateTimeAttributeNew {...props} />
			)
		) : (
			<DateTimeAttributeOld {...props} />
		)}
	</JSErrorBoundary>
);
